/**
 * localStorage 永続化つきコレクションのストア用ファクトリ。
 *
 * smokingStore / propertyStoreV2 でほぼ同型だったパターンを集約:
 * - deep watch で変更を localStorage へ自動保存
 * - load 時の型ガード (配列でなければ空) と警告
 * - get / insert / update (updatedAt 更新) / remove の基本 CRUD
 *
 * ストア固有の要素 (id 生成を含む create、seed 注入、upsert 等) は
 * 各ストア側に残す。propertyStore (v1) は SAMPLE_VERSION マイグレーションが
 * 複雑なため移行対象外。
 */
import { ref, watch, type Ref } from 'vue';
import { nowIso } from '@/lib/id';

export interface PersistedCrudOptions<T> {
  /** localStorage のキー (既存キーと完全一致させること)。 */
  storageKey: string;
  /** console.warn 用の対象名 (例: '物件データ(喫煙所)')。 */
  label: string;
  /** 初回 (localStorage 未作成 = raw === null) の初期値。既定は空配列。 */
  initial?: () => T[];
  /** load 時に各要素へ適用する正規化 (後方互換移行等)。省略時は素通し。 */
  normalize?: (item: T) => T;
}

export interface PersistedCrud<T extends { id: string }> {
  items: Ref<T[]>;
  get(id: string): T | undefined;
  /** 末尾に追加する (id/日時の付与は呼び出し側の責務)。 */
  insert(item: T): void;
  /** 部分更新 + updatedAt を現在時刻に更新する。 */
  update(id: string, patch: Partial<Omit<T, 'id' | 'createdAt'>>): void;
  remove(id: string): void;
}

export function createPersistedCrud<
  T extends { id: string; updatedAt: string },
>(opts: PersistedCrudOptions<T>): PersistedCrud<T> {
  function load(): T[] {
    try {
      const raw = localStorage.getItem(opts.storageKey);
      if (raw === null) return opts.initial?.() ?? [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const list = parsed as T[];
      return opts.normalize ? list.map(opts.normalize) : list;
    } catch (e) {
      console.warn(`${opts.label}の読み込みに失敗しました`, e);
      return [];
    }
  }

  const items = ref(load()) as Ref<T[]>;

  // 自動保存: items 変更を検知して localStorage に反映
  watch(
    items,
    (next) => {
      try {
        localStorage.setItem(opts.storageKey, JSON.stringify(next));
      } catch (e) {
        console.warn(`${opts.label}の保存に失敗しました`, e);
      }
    },
    { deep: true },
  );

  function get(id: string): T | undefined {
    return items.value.find((p) => p.id === id);
  }

  function insert(item: T): void {
    items.value.push(item);
  }

  function update(id: string, patch: Partial<Omit<T, 'id' | 'createdAt'>>): void {
    const idx = items.value.findIndex((p) => p.id === id);
    if (idx < 0) return;
    const existing = items.value[idx]!;
    items.value[idx] = {
      ...existing,
      ...patch,
      updatedAt: nowIso(),
    };
  }

  function remove(id: string): void {
    const idx = items.value.findIndex((p) => p.id === id);
    if (idx >= 0) items.value.splice(idx, 1);
  }

  return { items, get, insert, update, remove };
}
